'use client';

import { forgotPassword } from '@/features/auth/forgot-form/api/forgot-password';
import { FORGOT_SENT_PATHNAME } from '@/shared/constants/pathnames';
import { useRouter } from 'next/navigation';
import { useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useWatch } from 'react-hook-form';
import { Button, Text } from '@/shared/ui/core';
import { StatusMessage } from '@/shared/ui/core/status-message';
import { ClearableInput } from '@/shared/ui/kit/clearable-input';
import { forgotSchema, ForgotFormData } from '@/shared/lib/validation/forgot';
import styles from './forgot-form.module.scss';


export const ForgotForm = () => {
    const router                         = useRouter();
    const [ isPending, startTransition ] = useTransition();
    const [ hasError, setHasError ]        = useState(false);
    const { register, control, handleSubmit, setValue, clearErrors, formState: { errors } } = useForm<ForgotFormData>({
        resolver     : zodResolver(forgotSchema),
        mode         : 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: { email: '' },
    });

    const email = useWatch({ control, name: 'email' });

    const onSubmit = handleSubmit((data) => {
        setHasError(false);

        startTransition(async () => {
            try {
                await forgotPassword({ email: data.email });
                router.replace(`${ FORGOT_SENT_PATHNAME }?email=${ encodeURIComponent(data.email) }`);
            } catch {
                // бэкенд всегда отвечает 200 (в том числе для неизвестного адреса), ошибка — только сбой отправки
                setHasError(true);
            }
        });
    });

    return (
        <div data-template="forgot-form">
            {
                hasError &&
                <StatusMessage
                    status="destructive"
                    className={ styles.status }
                    title={
                        <Text
                            size="xl"
                            tone="destructive"
                            weight="semibold"
                            lineHeight={ 1 }
                        >
                            Error occurred
                        </Text>
                    }
                >
                    <Text
                        size="lg"
                        tone="destructive"
                        weight="regular"
                        lineHeight={ 1.2 }
                    >
                        We couldn&apos;t send the reset instructions. Please try again in a few minutes
                    </Text>
                </StatusMessage>
            }

            <ClearableInput
                label="E-mail:"
                placeholder="mail@example.com"
                autoComplete="email"
                type="email"
                error={ errors.email?.message }
                value={ email }
                onClear={ () => setValue('email', '', { shouldValidate: true }) }
                { ...register('email') }
                onFocus={ () => clearErrors('email') }
            />

            <Button
                fullWidth
                className={ styles.button }
                onClick={ onSubmit }
                loading={ isPending }
            >
                Send me reset instructions
            </Button>
        </div>
    );
};
